import { useState } from 'react';

export type InquiryCategory = 'inquiry' | 'suggestion';

const categoryOptions: { value: InquiryCategory; label: string; icon: string }[] = [
  { value: 'inquiry', label: '문의', icon: 'ri-question-line' },
  { value: 'suggestion', label: '건의사항', icon: 'ri-lightbulb-line' },
];

interface InquiryFormProps {
  onSubmit: (data: {
    category: InquiryCategory;
    title: string;
    content: string;
    email?: string;
  }) => Promise<boolean>;
  requireEmail?: boolean;
}

export default function InquiryForm({ onSubmit, requireEmail = false }: InquiryFormProps) {
  const [category, setCategory] = useState<InquiryCategory>('inquiry');
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [email, setEmail] = useState('');
  const [phoneAlt, setPhoneAlt] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');

    if (!title.trim()) {
      setError('제목을 입력해주세요.');
      return;
    }
    if (!content.trim()) {
      setError('내용을 입력해주세요.');
      return;
    }
    if (requireEmail) {
      if (!email.trim()) {
        setError('답변을 받을 이메일을 입력해주세요.');
        return;
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
        setError('올바른 이메일 주소를 입력해주세요.');
        return;
      }
    }

    // 스팸 방지용 허니팟: 숨겨진 필드에 값이 들어오면 봇으로 간주하고 조용히 성공 처리
    if (phoneAlt.trim()) {
      setTitle('');
      setContent('');
      setEmail('');
      return;
    }

    setLoading(true);
    const ok = await onSubmit({
      category,
      title: title.trim(),
      content: content.trim(),
      email: requireEmail ? email.trim() : undefined,
    });
    setLoading(false);

    if (ok) {
      setTitle('');
      setContent('');
      setEmail('');
    }
  };

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-background-200 p-5 md:p-6">
      <div className="flex items-center gap-2 mb-5">
        <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-100 text-primary-700">
          <i className="ri-chat-3-line text-lg"></i>
        </div>
        <h2 className="font-heading text-lg font-bold text-foreground-950">문의 작성</h2>
      </div>

      {/* 카테고리 선택 */}
      <div className="mb-4">
        <span className="block text-sm font-medium text-foreground-700 mb-1.5">유형</span>
        <div className="inline-flex bg-background-100 p-1 rounded-full">
          {categoryOptions.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setCategory(opt.value)}
              className={`whitespace-nowrap inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs sm:text-sm font-semibold transition-colors cursor-pointer ${
                category === opt.value
                  ? 'bg-white text-foreground-950 border border-background-300'
                  : 'text-foreground-500 hover:text-foreground-700'
              }`}
            >
              <i className={opt.icon}></i>
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* 이메일 (비로그인 시) */}
      {requireEmail && (
        <div className="mb-4">
          <label className="block">
            <span className="block text-sm font-medium text-foreground-700 mb-1.5">이메일</span>
            <input
              type="email"
              name="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-2.5 text-sm rounded-md border border-background-300 bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-300"
              placeholder="답변을 받으실 이메일 주소"
            />
          </label>
          <p className="text-xs text-foreground-400 mt-1">
            로그인 없이도 문의할 수 있어요. 답변은 이메일로 안내드립니다.
          </p>
        </div>
      )}

      {/* 스팸 방지 허니팟 (사용자에게 보이지 않음) */}
      <div className="hp-field" aria-hidden="true">
        <label>
          <span>휴대폰</span>
          <input
            type="text"
            name="phone_alt"
            tabIndex={-1}
            autoComplete="off"
            readOnly
            value={phoneAlt}
            onChange={(e) => setPhoneAlt(e.target.value)}
          />
        </label>
      </div>

      {/* 제목 */}
      <div className="mb-4">
        <label className="block">
          <span className="block text-sm font-medium text-foreground-700 mb-1.5">제목</span>
          <input
            type="text"
            name="title"
            required
            maxLength={100}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full px-4 py-2.5 text-sm rounded-md border border-background-300 bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-300"
            placeholder="문의 제목을 입력해주세요"
          />
        </label>
      </div>

      {/* 내용 */}
      <div className="mb-4">
        <label className="block">
          <span className="block text-sm font-medium text-foreground-700 mb-1.5">내용</span>
          <textarea
            name="content"
            required
            maxLength={500}
            rows={5}
            value={content}
            onChange={(e) => setContent(e.target.value)}
            className="w-full px-4 py-2.5 text-sm rounded-md border border-background-300 bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-300 resize-none"
            placeholder="문의하실 내용을 자세히 적어주세요"
          />
        </label>
        <div className="text-right text-xs text-foreground-400 mt-1">{content.length}/500</div>
      </div>

      {error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2 mb-4">
          {error}
        </div>
      )}

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <p className="text-xs text-foreground-500">
          운영자가 확인 후 답변을 등록해드립니다.
          {requireEmail
            ? ' 비로그인 문의는 답변을 이메일로 안내드립니다.'
            : ' 답변은 이 페이지에서 확인하실 수 있어요.'}
        </p>
        <button
          type="submit"
          disabled={loading}
          className="whitespace-nowrap shrink-0 bg-primary-500 hover:bg-primary-600 text-white font-semibold px-6 py-2.5 rounded-md transition-colors cursor-pointer disabled:opacity-60 text-sm"
        >
          {loading ? '접수 중...' : '문의 접수하기'}
        </button>
      </div>
    </form>
  );
}